import React, {PropsWithChildren, useEffect, useState, useRef} from 'react';
import withAuthentication from '@/middleware/authentication';
import {RootStoreType} from '@/store/root-store';
import {Layout as AntdLayout} from 'antd';
import Header from '@/components/layoutV2/header';
import styles from '@/components/layoutV2/style.module.scss';
import {checkWLChildrenPagesStatus, WLchildrenPagesMapping, meetingModalExcludedPages} from '@/utils/const';
import {CommonSidebar} from '@/components/layoutV2/commonSidebar';
import {isNavbarRemoveOnPublicPages, copyAndModifyQueryParams, getSingleUrlParam} from '@/utils/url';
import moment from 'moment/moment';
import {getTokenFromCookies} from '@/api/common-utils';
import userpilot from '@/utils/userpilot/userpilot';
import {updateGTMDatalayer} from '@/utils/gtm';
import {apiError} from '@/utils/api';
import {notification} from '@/utils/notification';
import {getHasRegisteredFromStorage} from '@/api/common-utils';
import {SpinnerWrapper} from '@/components/common-components/v2/maps/universalLoader/styled';
import dynamic from 'next/dynamic';
import {observer} from 'mobx-react-lite';
import {useRouter} from 'next/router';
import {MainLoader} from '@/components/common-components/v2/MainLoader';
import CheckoutHeader from '../header/checkoutHeader';
import {LayoutModalsAndDrawers} from './components/LayoutModalsAndDrawers';

type PrivateLayoutProps = PropsWithChildren<{
  store: RootStoreType;
}>;

const DynamicServiceHealthAlert = dynamic(() => import('@/components/dashboard/serviceHealthAlert').then(mod => mod.ServiceHealthAlert), {
  loading: () => <SpinnerWrapper/>,
  ssr: false,
});

export const PrivateLayout: React.FC<PrivateLayoutProps> = withAuthentication(
  observer<PrivateLayoutProps>(({store, children}) => {
    const router = useRouter();
    const {query, asPath, pathname: path} = router;
    const {
      settings: {customer: {WLchildrenPages, loadWLChildrenAccessPages, apiResponseReceived, isGHLAccount, profile, loadUserOnboardingData, getPostOnboardingNotifications, onboardingApiResponseReceived, getCustomersPlanOptions}},
      gsc: {isDrawerVisible, setDrawerVisibility, setIsTeamMember, isSettingModalVisible, setSettingModalVisible},
      plans: {getSidebarPayment},
      competitorResearcherV2: {overview: {outDomainsVisible}},
      home: {isChild},
    } = store;
    const {hasOnboardingCompleted, isSubscriber, planName, paymentFailed, trialExpiresAt, isWhitelabel, isParentAccount, emailAddress, addons, joiningDate, customerId: profileCustomerId} = profile;

    const checkoutPages = ['/order-checkout', '/checkout', '/order-builder'];
    const [isHiddenBar, setIsHiddenBar] = useState(false);
    const [sideBarItems, setSideBarItems] = useState([]);
    const [isCheckoutPage, setIsCheckoutPage] = useState(checkoutPages.includes(path));
    const isOnBoarding = (path === '/onboarding' || path === '/choose-plan' || path === '/choose-plan/checkout' || path === '/onboarding/[step]' || path === '/account-deactivated' || path === '/request-trial');
    const contentRef = useRef(null);
    const {Embed, customerId: queryCustomerId} = query;

    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showOnboardingPromotionModal, setShowOnboardingPromotionModal] = useState(false);

    const [isBrowser, setIsBrowser] = useState(false);
    const [currentCustomerId, setCurrentCustomerId] = useState<string | null>(null);

    const getDiffDays = (firstDate: string | Date, secondDate: string | Date): number => {
      const date1 = new Date(firstDate);
      const date2 = new Date(secondDate);
      date1.setHours(0, 0, 0, 0);
      date2.setHours(0, 0, 0, 0);
      const diffTime = Math.abs(date2.getTime() - date1.getTime());
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      return Math.ceil(diffDays);
    };

    const formatAccountID = (accountID: number | undefined): string => {
      if (accountID === undefined) return '-';
      const accountIDString = accountID.toString();
      const formattedID = accountIDString.replace(/(\d{3})(?=\d)/g, '$1-');
      return formattedID;
    };

    const getDataFromAPi = async () => {
      try {
        if (profileCustomerId) {
          if (!getPostOnboardingNotifications?.length && !onboardingApiResponseReceived) {
            await loadUserOnboardingData({channels: 'onboarding'});
          }
          await loadUserOnboardingData({channels: 'general'});
        }
      } catch (e) {
        const errorMessage = apiError(e) as string;
        notification.error('', errorMessage);
      }
    };


    useEffect(() => {
      const today = new Date();
      const dd = today.getDate();
      const mm = today.getMonth();
      const yy = today.getFullYear();
      if (joiningDate && !isWhitelabel) {
        const creationDate = joiningDate;
        const currentDate = `${mm + 1}/${dd}/${yy}`;
        const alreadyBooked = JSON.parse(localStorage.getItem('onboardingCallBooked') || 'false');
        if (!alreadyBooked) {
          if (getDiffDays(currentDate, creationDate) <= 14) {
            const previousShownDate = localStorage.getItem('callModalShownDate');
            if (!previousShownDate || (previousShownDate && getDiffDays(currentDate, previousShownDate) >= 1)) {
              setShowOnboardingPromotionModal(true);
            }
          }
        }
      }
    }, [joiningDate, isWhitelabel]);

    useEffect(() => {
      const GSCIntegration = getSingleUrlParam('active_tab');
      const googleAnalytics = getSingleUrlParam('google-analytics');
      const googleBusiness = getSingleUrlParam('google-business');
      const getToken = getTokenFromCookies();
      if ((GSCIntegration || googleAnalytics || googleBusiness) && getToken) {
        setDrawerVisibility(true);
      }
    }, []);

    useEffect(() => {
      const hasRegistered = getHasRegisteredFromStorage();
      if (hasRegistered === 'YES' && profileCustomerId) {
        if (query['active_modal'] === 'settings') {
          setSettingModalVisible(true);
        } else {
          setSettingModalVisible(false);
        }
      }
      if (hasRegistered === 'YES' || getTokenFromCookies()) {
        getDataFromAPi();
      }
    }, [query, profileCustomerId, onboardingApiResponseReceived, getPostOnboardingNotifications?.length]);

    useEffect(() => {
      if (asPath?.includes('customerSwitcherModal')) {
        setShowCustomerModal(true);
        getCustomersPlanOptions();
      } else {
        setShowCustomerModal(false);
      }
    }, [asPath, getCustomersPlanOptions]);

    useEffect(() => {
      if (Embed && Embed == 'True') {
        setIsHiddenBar(true);
      }
      setIsCheckoutPage(checkoutPages.includes(path));
    }, [query, path, Embed]);

    useEffect(() => {
      userpilot.reload();
    }, [router]);

    useEffect(() => {
      setIsBrowser(true);
      if (typeof window !== 'undefined') {
        setCurrentCustomerId(localStorage.getItem('currentCustomerId'));
      }
    }, []);

    const checkUserPlan = () => {
      const trialExpiresAtDateCheck = !moment(trialExpiresAt)?.isValid();
      return !isNavbarRemoveOnPublicPages() && ((paymentFailed || planName?.toLowerCase().trim() == 'free' || (trialExpiresAtDateCheck && !isSubscriber)) && !asPath?.match(/order|pricing|billing|checkout/g));
    };

    useEffect(() => {
      if (checkUserPlan() && hasOnboardingCompleted === true) {
        router.push('/billing');
      }
      contentRef?.current?.scrollTo(0, 0);
    }, [path, planName, paymentFailed, trialExpiresAt, hasOnboardingCompleted, isSubscriber]);

    useEffect(() => {
      if (hasOnboardingCompleted === false && path !== '/onboarding' && path !== '/choose-plan' && path !== '/choose-plan/checkout' && path !== '/account-deactivated' && path !== '/request-trial') { // Порівняння з false
        const token = getTokenFromCookies();
        if (token && !isSubscriber && !trialExpiresAt && !isWhitelabel) {
          router.push('/choose-plan');
        } else if (isWhitelabel && !isParentAccount && hasOnboardingCompleted === false) {
          router.push('/account-deactivated');
        } else if (token) {
          router.push('/onboarding');
        }
      }
      if (hasOnboardingCompleted !== false) {
        loadWhiteLabelPages();
      }
    }, [hasOnboardingCompleted, path, isSubscriber, trialExpiresAt, isWhitelabel, isParentAccount]);

    const loadWhiteLabelPages = async () => {
      if (isWhitelabel && hasOnboardingCompleted !== false) {
        await loadWLChildrenAccessPages();
      }
    };

    useEffect(() => {
      loadWhiteLabelPages();
    }, [isWhitelabel, hasOnboardingCompleted, loadWLChildrenAccessPages]);


    useEffect(() => {
      if (!isGHLAccount) {
        if (apiResponseReceived) {
          if (!isParentAccount && isWhitelabel && Object.keys(WLchildrenPages || {})?.length > 0) {
            const currentPath = path;
            if (checkWLChildrenPagesStatus(currentPath, WLchildrenPages)) return;
            const keys = Object.keys(WLchildrenPagesMapping);
            const currentIndex = keys.indexOf(currentPath);

            let foundNext = false;
            for (let i = currentIndex + 1; i < keys.length; i++) {
              const nextKey = keys[i];
              if (checkWLChildrenPagesStatus(nextKey, WLchildrenPages)) {
                router.push(`${nextKey}${queryCustomerId ? `?customerId=${queryCustomerId}` : ''}`);
                foundNext = true;
                break;
              }
            }
            if (!foundNext) {
              const validKey = keys.find(key => checkWLChildrenPagesStatus(key, WLchildrenPages));
              if (validKey) {
                router.push(`${validKey}${queryCustomerId ? `?customerId=${queryCustomerId}` : ''}`);
              } else {
                if (path !== '/billing') {
                  router.push('/billing');
                }
              }
            }
          }
        }
      }
    }, [WLchildrenPages, queryCustomerId, apiResponseReceived, path, isParentAccount, isWhitelabel, isGHLAccount]);

    useEffect(() => {
      if (profile?.id) {
        const {triggerGoogleAddonGaEvent, triggerSAFreeTrialGaEvent} = query;
        const commonData = {
          'email': emailAddress,
          'plan': planName,
          'price': profile?.subscription?.plan?.monthlyCost,
          'currency_code': profile?.subscription?.plan?.monthlyCostCurrency || 'USD',
          'status': 'success',
        };
        if (triggerGoogleAddonGaEvent) {
          const addon = addons?.find(addon => addon.planId == 1030);
          if (addon) {
            updateGTMDatalayer({'event': 'purchase', ...commonData});
            updateGTMDatalayer({'event': 'purchase OTTO plan', ...{...commonData, 'plan': addon.planName, 'price': addon.paymentAmount}});
          }
          const newURL = copyAndModifyQueryParams([
            {key: 'triggerGoogleAddonGaEvent', value: null},
          ]);

          router.replace(newURL, undefined, {shallow: true});
        }

        if (triggerSAFreeTrialGaEvent) {
          updateGTMDatalayer({'event': 'purchase', ...commonData});
          const newURL = copyAndModifyQueryParams([
            {key: 'triggerSAFreeTrialGaEvent', value: null},
          ]);

          router.replace(newURL, undefined, {shallow: true});
        }
      }
    }, [query, profile?.id, emailAddress, planName, profile?.subscription, addons, router]);

    const isImpersonate = (localStorage.getItem('impersonate') || false);

    return (
      <>
        {isOnBoarding ? (
          <AntdLayout className={styles.layout}>{children}</AntdLayout>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
            <DynamicServiceHealthAlert />
            {isImpersonate && <DynamicServiceHealthAlert showImpersonateAlert />}
            <div style={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
              {!isHiddenBar && (isCheckoutPage ? <CheckoutHeader store={store} /> : <Header store={store} handleSideBarItems={items => setSideBarItems(items)} />)}
              {checkUserPlan() ? <MainLoader /> :
                <div style={{display: 'flex', flexGrow: 1}}>
                  <div id='SIDEBAR_MAIN' style={{height: 'auto', background: '#0c0e12', position: 'relative'}}>
                    {isBrowser && !isHiddenBar && !isCheckoutPage &&
                    <CommonSidebar sideBarItems={sideBarItems} publicPage={false} />
                    }
                  </div>
                  <div style={{flexGrow: '1', transition: 'all 0.2s ease', overflow: 'hidden'}} ref={contentRef}>
                    <div>{children}</div>
                  </div>
                </div>
              }
            </div>
          </div>
        )}
        <LayoutModalsAndDrawers
          store={store}
          router={router}
          isDrawerVisible={isDrawerVisible}
          onCloseSettingsDrawer={() => {
            setIsTeamMember(false);
            setDrawerVisibility(false);
            const queryParam = isChild && currentCustomerId ?
              [{key: 'customerId', value: currentCustomerId}] :
              [{key: '', value: ''}];
            router.push(copyAndModifyQueryParams(queryParam), undefined, {
              shallow: true,
            });
          }}
          isSettingModalVisible={isSettingModalVisible}
          onCloseSettingsModal={() => {
            const {query: currentQuery} = router;
            const updatedQuery = {...currentQuery};
            delete updatedQuery['active_modal'];
            delete updatedQuery['active_section'];
            router.replace({
              pathname: path,
              query: updatedQuery,
            }, undefined, {shallow: true});
            setSettingModalVisible(false);
          }}
          profileCustomerId={profileCustomerId}
          formatAccountID={formatAccountID}
          getSidebarPayment={getSidebarPayment}
          outDomainsVisible={outDomainsVisible}
          showCustomerModal={showCustomerModal}
          setShowCustomerModal={setShowCustomerModal}
          showOnboardingPromotionModal={showOnboardingPromotionModal}
          setShowOnboardingPromotionModal={setShowOnboardingPromotionModal}
          hasOnboardingCompleted={hasOnboardingCompleted}
          meetingModalExcludedPages={meetingModalExcludedPages}
          currentPath={path}
        />
      </>
    );
  }),
);
